import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useInterval } from 'react-use';

import { type GetTaskResponse, type TaskType as RemoteTaskType } from '@air/api/types';
import { THIRTY_SECONDS } from '@air/utilities';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useEventSubscription } from '~/hooks/useEventSubscription';
import {
  markTaskAsCompletedAction,
  markTaskAsFailedAction,
  removeTasksAction,
  setTaskAction,
  updateTaskMetadataAction,
} from '~/store/tasks/actions';
import {
  type GetCompletedMetadata,
  type GetInProgressMetadata,
  type GetTaskByType,
  type TaskType as LocalTaskType,
  type TaskType,
} from '~/store/tasks/types';
import { type TaskStatus } from '~/store/tasks/typeUtils';
import { type AirState } from '~/store/types';
import { useAirSelector, useAirStore } from '~/utils/ReduxUtils';
import {
  getTasksFromLocalStorage,
  removeTasksFromLocalStorage,
  updateTaskInLocalStorage,
} from '~/utils/taskUtils/storage';
import { useSettledTaskClaims } from '~/utils/taskUtils/useSettledTaskClaims';

export const UNSETTLED_TASK_POLL_INTERVAL = THIRTY_SECONDS;

/**
 * A task whose sync keeps throwing — purged server side, no permission, a consistently failing
 * `onComplete` — would otherwise be retried, and reported, every interval for the life of the page.
 */
export const MAX_CONSECUTIVE_SYNC_FAILURES = 5;

/**
 * A task is unsettled while the server is still the source of truth for its outcome — it has been
 * created remotely but has not yet reached a terminal status locally.
 */
const isUnsettledTask = ({ remoteTaskId, status }: { remoteTaskId: string | undefined; status: TaskStatus }) =>
  !!remoteTaskId && (status === 'pending' || status === 'in-progress');

type TasksSelector<T extends TaskType> = ({ tasks }: AirState) => GetTaskByType<T>[];

type GetTaskResponseByType<T extends RemoteTaskType> = Extract<GetTaskResponse, { type: T }>;

type NormalizedTaskFromServer<T extends RemoteTaskType> = Pick<
  GetTaskResponseByType<T>,
  'type' | 'id' | 'attempts' | 'completedAt'
> & {
  data?: GetTaskResponseByType<T>['data'];
  args?: GetTaskResponseByType<T>['args'];
};

export type SharedSyncTasksParams<LocalType extends LocalTaskType, RemoteType extends RemoteTaskType> = {
  localType: LocalType;
  remoteType: RemoteType;
  onComplete?: (params: {
    task: NormalizedTaskFromServer<RemoteType>;
    localTask: GetTaskByType<LocalType>;
    enrichMetadata: (metadata: Partial<GetCompletedMetadata<GetTaskByType<LocalType>, false>>) => void;
  }) => Promise<void> | void;
  onUpdate?: (params: {
    task: NormalizedTaskFromServer<RemoteType>;
    localTask: GetTaskByType<LocalType>;
    enrichMetadata: (metadata: Partial<GetInProgressMetadata<GetTaskByType<LocalType>>>) => void;
  }) => Promise<void> | void;
  onError?: (params: { task: NormalizedTaskFromServer<RemoteType>; localTask: GetTaskByType<LocalType> }) => void;
  tasksSelector: TasksSelector<LocalType>;
  getTask: (taskId: string) => Promise<GetTaskResponse>;
  workspaceId?: string;
};

export const useSharedSyncTasks = <
  LocalType extends LocalTaskType = LocalTaskType,
  RemoteType extends RemoteTaskType = RemoteTaskType,
>({
  localType,
  remoteType,
  tasksSelector,
  getTask,
  workspaceId,
  onComplete,
  onUpdate,
  onError,
}: SharedSyncTasksParams<LocalType, RemoteType>) => {
  type CurrentTaskType = GetTaskByType<LocalType>;

  const dispatch = useDispatch();
  const store = useAirStore();

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  /**
   * A task's terminal side effects (`onComplete` / `onError`) must run at most once: a
   * `System.Task.completed` event and a poll can both observe the same finished task. Whichever
   * gets there first claims the settlement; the claim is released if settling throws, so a later
   * sync can retry. Keyed by remote id because the claim describes a server task's outcome, not
   * the local record that happens to be tracking it.
   *
   * The claim must be held if and only if the task actually settled, so every branch dispatches
   * before touching local storage: a claim held over an unsettled task is unrecoverable, since
   * later polls and events both exit at the claim check.
   */
  const { claimTaskSettlement, hasSettledTask, releaseTaskSettlement, seedSettledTasks } = useSettledTaskClaims();

  /**
   * Tasks already settled when this instance started watching a workspace are history: nobody is
   * waiting on callbacks we were not around to owe. Re-seeded per workspace because `workspaceId`
   * arrives late in the asset modal — `centralizedClip` starts with an empty one — so a mount-time
   * seed would be empty and every settled task would look like ours to catch up on.
   *
   * Keyed on `workspaceId` rather than `tasksSelector`: a consumer passing an unmemoized selector
   * would re-seed on every render and silently swallow another instance's settlements.
   */
  const seededWorkspaceIdRef = useRef<string>();

  if (seededWorkspaceIdRef.current !== workspaceId) {
    seededWorkspaceIdRef.current = workspaceId;

    seedSettledTasks(
      tasksSelector(store.getState())
        .filter((task) => !isUnsettledTask(task))
        .flatMap(({ remoteTaskId }) => (remoteTaskId ? [remoteTaskId] : [])),
    );
  }

  /**
   * A task this instance has not settled itself. Redux status can't answer this: a second instance
   * of the hook syncing the same tasks — the two `AssetTransformation` consumers are both mounted
   * whenever an asset modal is open — settles the shared task with *its* callbacks, and we still
   * owe ours. Our own claim is what tells us we are done with it.
   */
  const isUnsyncedTask = useCallback(
    ({ remoteTaskId }: { remoteTaskId: string | undefined }) => !!remoteTaskId && !hasSettledTask(remoteTaskId),
    [hasSettledTask],
  );

  /** Keeps overlapping syncs (poll tick, tab refocus, socket reconnect) from re-fetching a task. */
  const inFlightLocalTaskIdsRef = useRef(new Set<string>());

  /** Consecutive failed sync attempts per remote task, reset by any attempt that completes. */
  const consecutiveSyncFailuresRef = useRef(new Map<string, number>());

  const pollUnsettledTasks = useCallback(() => {
    if (!workspaceId) {
      return;
    }

    const tasks = tasksSelector(store.getState()).filter(isUnsyncedTask);

    tasks.forEach((localTask) => {
      const { localTaskId, remoteTaskId } = localTask;

      // The filter above already guaranteed a remote id; this narrows it for TypeScript
      if (!remoteTaskId || inFlightLocalTaskIdsRef.current.has(localTaskId)) {
        return;
      }

      if ((consecutiveSyncFailuresRef.current.get(remoteTaskId) ?? 0) >= MAX_CONSECUTIVE_SYNC_FAILURES) {
        return;
      }

      inFlightLocalTaskIdsRef.current.add(localTaskId);

      getTask(remoteTaskId)
        .then(async (remoteTask) => {
          if (remoteTask.type !== remoteType) {
            return;
          }

          // A completion event can settle this task while the fetch is in flight, so the fresh copy
          // is spread below rather than the pre-fetch snapshot — `setTaskAction` replaces the whole
          // task — and we stop if this instance settled it while the fetch was out.
          const currentTask = tasksSelector(store.getState()).find((task) => task.localTaskId === localTaskId);

          if (!currentTask || !isUnsyncedTask(currentTask)) {
            return;
          }

          // Only refresh a task still running. Another instance may have settled it while this
          // fetch was out, and this response is older than the metadata and timestamps it wrote.
          // The switch below still runs, so this instance can settle it and owe its own callbacks.
          if (isUnsettledTask(currentTask)) {
            dispatch(
              setTaskAction({
                ...currentTask,
                ...(currentTask.status === 'pending' && remoteTask.status === 'active'
                  ? { status: 'in-progress' }
                  : {}),
                startedAt: remoteTask.startedAt,
                createdAt: remoteTask.createdAt,
                updatedAt: remoteTask.updatedAt,
                metadata: {
                  ...currentTask.metadata,
                  ...remoteTask.data,
                },
              }),
            );
          }

          switch (remoteTask.status) {
            case 'exception':
            case 'failed':
              if (!claimTaskSettlement(remoteTaskId)) {
                break;
              }

              try {
                dispatch(
                  markTaskAsFailedAction({
                    localTaskId,
                  }),
                );
              } catch (error) {
                releaseTaskSettlement(remoteTaskId);
                throw error;
              }

              onErrorRef.current?.({
                task: remoteTask as unknown as NormalizedTaskFromServer<RemoteType>,
                localTask: currentTask,
              });
              break;

            case 'succeeded': {
              if (!claimTaskSettlement(remoteTaskId)) {
                break;
              }

              const enricher = makeTaskMetadataEnricher<GetCompletedMetadata<CurrentTaskType, false>>();

              try {
                await onCompleteRef.current?.({
                  task: remoteTask as unknown as NormalizedTaskFromServer<RemoteType>,
                  localTask: currentTask,
                  enrichMetadata: enricher.set,
                });

                dispatch(
                  markTaskAsCompletedAction({
                    localTaskId,
                    metadata: enricher.get() ?? {},
                  }),
                );
              } catch (error) {
                releaseTaskSettlement(remoteTaskId);
                throw error;
              }

              removeTasksFromLocalStorage([localTaskId]);
              break;
            }

            case 'cancelled': {
              if (!claimTaskSettlement(remoteTaskId)) {
                break;
              }

              try {
                dispatch(removeTasksAction({ taskIds: [localTaskId] }));
              } catch (error) {
                releaseTaskSettlement(remoteTaskId);
                throw error;
              }

              removeTasksFromLocalStorage([localTaskId]);
              break;
            }
          }
        })
        .then(() => {
          consecutiveSyncFailuresRef.current.delete(remoteTaskId);
        })
        .catch((error) => {
          const consecutiveFailures = (consecutiveSyncFailuresRef.current.get(remoteTaskId) ?? 0) + 1;
          consecutiveSyncFailuresRef.current.set(remoteTaskId, consecutiveFailures);

          reportErrorToBugsnag({
            error,
            context: 'Failed to get task useSharedSyncTasks',
            metadata: {
              data: {
                remoteTaskId,
                consecutiveFailures,
                stoppedPolling: consecutiveFailures >= MAX_CONSECUTIVE_SYNC_FAILURES,
              },
            },
          });
        })
        .finally(() => {
          inFlightLocalTaskIdsRef.current.delete(localTaskId);
        });
    });
  }, [
    claimTaskSettlement,
    dispatch,
    getTask,
    isUnsyncedTask,
    releaseTaskSettlement,
    remoteType,
    store,
    tasksSelector,
    workspaceId,
  ]);

  /**
   * The catch-up sync: what consumers run on mount and on socket reconnect, and what the listeners
   * run when the tab comes back. Every one of those is a "something changed, try again" signal, so
   * a task that exhausted its retries gets a fresh start — otherwise five failures inside a bad two
   * minutes strand it for the life of the page, which is the hang this hook exists to prevent.
   */
  const syncLocalTasks = useCallback(() => {
    consecutiveSyncFailuresRef.current.clear();
    pollUnsettledTasks();
  }, [pollUnsettledTasks]);

  const loadFromStorage = useCallback(() => {
    const tasks = getTasksFromLocalStorage().filter((task) => task.type === localType);
    tasks.forEach((localStorageTask) => {
      dispatch(setTaskAction(localStorageTask));
    });
    syncLocalTasks();
  }, [dispatch, syncLocalTasks, localType]);

  // Task initially has pending status, so we need to subscribe to the started event to update the task status to in-progress
  const { isSubscribed: isStartedSubscriptionReady } = useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: {
      eventType: 'System.Task.started',
      pattern: {
        type: remoteType,
      },
    },
    options: {
      onUpdate: async (message) => {
        const taskId = message.data.id;
        const tasks = tasksSelector(store.getState());

        const localTask = tasks.find(
          (taskCandidate): taskCandidate is CurrentTaskType => taskCandidate.remoteTaskId === taskId,
        );

        if (!localTask || !isUnsettledTask(localTask)) {
          return;
        }

        const enricher = makeTaskMetadataEnricher<GetInProgressMetadata<CurrentTaskType>>();

        await onUpdateRef.current?.({
          task: message.data as unknown as NormalizedTaskFromServer<RemoteType>,
          localTask,
          enrichMetadata: enricher.set,
        });

        // The task can settle while `onUpdate` runs. `setTaskAction` replaces the whole task, so
        // writing `in-progress` back would revert it, and the settlement claim would then refuse
        // to complete it again.
        const currentTask = tasksSelector(store.getState()).find((task) => task.localTaskId === localTask.localTaskId);

        if (!currentTask || !isUnsettledTask(currentTask)) {
          return;
        }

        updateTaskInLocalStorage<CurrentTaskType>({
          ...currentTask,
          status: 'in-progress',
        });

        dispatch(
          setTaskAction({
            ...currentTask,
            status: 'in-progress',
            startedAt: message.data.startedAt,
          }),
        );
      },
    },
  });

  const { isSubscribed: isUpdateSubscriptionReady } = useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: {
      eventType: 'System.Task.updated',
      pattern: {
        type: remoteType,
      },
    },
    options: {
      onUpdate: async (message) => {
        const taskId = message.data.id;
        const tasks = tasksSelector(store.getState());

        const localTask = tasks.find(
          (taskCandidate): taskCandidate is CurrentTaskType => taskCandidate.remoteTaskId === taskId,
        );

        if (!localTask || !isUnsettledTask(localTask)) {
          return;
        }

        const enricher = makeTaskMetadataEnricher<GetInProgressMetadata<CurrentTaskType>>();

        await onUpdateRef.current?.({
          task: message.data as unknown as NormalizedTaskFromServer<RemoteType>,
          localTask,
          enrichMetadata: enricher.set,
        });

        const metadata =
          message.data.data || enricher.get()
            ? {
                ...message.data.data,
                ...enricher.get(),
              }
            : undefined;

        if (metadata) {
          // Typescript error is suppressed because the task type is a union of every possible task type along with every asset transformation subtype
          // @ts-expect-error: Expression produces a union type that is too complex to represent.
          updateTaskInLocalStorage<CurrentTaskType>({
            ...localTask,
            status: 'in-progress',
            metadata: {
              ...localTask.metadata,
              ...message.data.data,
              ...metadata,
            },
          });

          dispatch(
            updateTaskMetadataAction({
              localTaskId: localTask.localTaskId,
              metadata,
            }),
          );
        }
      },
    },
  });

  const { isSubscribed: isCompletedSubscriptionReady } = useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: {
      eventType: 'System.Task.completed',
      pattern: {
        type: remoteType,
      },
    },
    options: {
      onUpdate: async (message) => {
        const tasks = tasksSelector(store.getState());

        const remoteTask = message.data as unknown as NormalizedTaskFromServer<RemoteType>;

        const remoteTaskId = message.data.id;

        const localTask = tasks.find((task) => task.remoteTaskId === remoteTaskId);

        if (localTask) {
          const { localTaskId } = localTask;

          if (!claimTaskSettlement(remoteTaskId)) {
            return;
          }

          if (message.data.status === 'failed' || message.data.status === 'exception') {
            try {
              dispatch(
                markTaskAsFailedAction({
                  localTaskId,
                }),
              );
            } catch (error) {
              releaseTaskSettlement(remoteTaskId);
              reportErrorToBugsnag({
                error,
                context: 'Failed to fail task useSharedSyncTasks',
              });

              return;
            }

            removeTasksFromLocalStorage([localTaskId]);
            onErrorRef.current?.({ task: remoteTask, localTask });
          } else {
            const enricher = makeTaskMetadataEnricher<GetCompletedMetadata<CurrentTaskType, false>>();

            try {
              await onCompleteRef.current?.({
                task: remoteTask,
                localTask,
                enrichMetadata: enricher.set,
              });

              dispatch(
                markTaskAsCompletedAction({
                  localTaskId,
                  metadata: {
                    ...remoteTask.data,
                    ...enricher.get(),
                  },
                }),
              );
            } catch (error) {
              // Leave the task unsettled — and in local storage — so the poll can complete it on a
              // later pass, or a reload can pick it back up.
              releaseTaskSettlement(remoteTaskId);
              reportErrorToBugsnag({
                error,
                context: 'Failed to complete task useSharedSyncTasks',
              });

              return;
            }

            removeTasksFromLocalStorage([localTaskId]);
          }
        }
      },
    },
  });

  /** Recomputed on store changes, and on the re-render `useSyncExternalStore` schedules for claims. */
  const hasUnsyncedTasks = useAirSelector((state) => tasksSelector(state).some(isUnsyncedTask));

  /**
   * `System.Task.completed` is the primary completion signal, but a dropped event used to leave a
   * task spinning forever with no path back to the truth (ONC-611). While anything is unsettled,
   * re-read task state on an interval and whenever the tab returns to the foreground — a tab that
   * slept through the event is the common case. Polling stops as soon as nothing is unsettled — or,
   * for a task that exhausts `MAX_CONSECUTIVE_SYNC_FAILURES`, it stays visible but is no longer
   * fetched by the interval, so ticks keep firing while every one skips it. Returning to the
   * foreground or reconnecting clears those failures, so the give-up is never permanent.
   */
  const isPollingEnabled = !!workspaceId && hasUnsyncedTasks;

  useInterval(
    () => {
      // Nothing in a hidden tab is waiting on this, and it comes back to a foreground re-sync anyway.
      if (document.visibilityState === 'hidden') {
        return;
      }

      pollUnsettledTasks();
    },
    isPollingEnabled ? UNSETTLED_TASK_POLL_INTERVAL : null,
  );

  useEffect(() => {
    if (!isPollingEnabled) {
      return;
    }

    const syncWhenForegrounded = () => {
      if (document.visibilityState === 'visible') {
        syncLocalTasks();
      }
    };

    document.addEventListener('visibilitychange', syncWhenForegrounded);
    window.addEventListener('focus', syncWhenForegrounded);

    return () => {
      document.removeEventListener('visibilitychange', syncWhenForegrounded);
      window.removeEventListener('focus', syncWhenForegrounded);
    };
  }, [isPollingEnabled, syncLocalTasks]);

  // All task-related subscriptions must be ready for safe task syncing
  const subscriptionsReady = isStartedSubscriptionReady && isUpdateSubscriptionReady && isCompletedSubscriptionReady;

  return { syncLocalTasks, loadFromStorage, subscriptionsReady };
};

function makeTaskMetadataEnricher<T>() {
  const value = {
    current: undefined as Partial<T> | undefined,
  };

  return {
    set: (metadata: Partial<T>) => {
      value.current = metadata;
    },
    get: () => value.current,
  };
}
